/**
 * Metadata Extractor Service
 * Extracts ID3 tags and other metadata from audio files
 */

export interface ExtractedMetadata {
  artist?: string;
  album?: string;
  albumArtist?: string;
  title?: string;
  genre?: string;
  year?: number;
  trackNumber?: number;
  discNumber?: number;
  composer?: string;
  comment?: string;
}

class MetadataExtractor {
  /**
   * Extract metadata from an audio file
   */
  async extractMetadata(file: File): Promise<ExtractedMetadata> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // Try to extract ID3v2 tags (most common)
      const id3v2 = this.extractID3v2(uint8Array);
      if (id3v2 && Object.keys(id3v2).length > 0) {
        return id3v2;
      }

      // Try to extract ID3v1 tags (fallback)
      const id3v1 = this.extractID3v1(uint8Array);
      if (id3v1 && Object.keys(id3v1).length > 0) {
        return id3v1;
      }

      return {};
    } catch (error) {
      console.error('Metadata extraction error:', error);
      return {};
    }
  }

  /**
   * Extract ID3v2 tags
   */
  private extractID3v2(data: Uint8Array): ExtractedMetadata {
    const metadata: ExtractedMetadata = {};

    // Check for ID3v2 header (first 3 bytes should be "ID3")
    if (data[0] !== 0x49 || data[1] !== 0x44 || data[2] !== 0x33) {
      return metadata;
    }

    const version = data[3];
    const flags = data[5];
    
    // Calculate tag size (synchsafe integer)
    const tagSize = this.getSynchsafeInt32(data.slice(6, 10));

    let offset = 10;
    const endOffset = Math.min(10 + tagSize, data.length);

    while (offset < endOffset - 10) {
      // Frame ID (4 bytes)
      const frameId = String.fromCharCode(data[offset], data[offset + 1], data[offset + 2], data[offset + 3]);
      offset += 4;

      // Frame size
      let frameSize: number;
      if (version === 4) {
        frameSize = this.getSynchsafeInt32(data.slice(offset, offset + 4));
      } else {
        frameSize = this.getInt32(data.slice(offset, offset + 4));
      }
      offset += 4;

      // Frame flags (skip)
      offset += 2;

      if (frameSize === 0 || frameSize > endOffset - offset) {
        break;
      }

      // Extract frame content
      const frameData = data.slice(offset, offset + frameSize);
      const value = this.decodeTextFrame(frameData);

      // Map frame IDs to metadata fields
      switch (frameId) {
        case 'TIT2':
          metadata.title = value;
          break;
        case 'TPE1':
          metadata.artist = value;
          break;
        case 'TALB':
          metadata.album = value;
          break;
        case 'TPE2':
          metadata.albumArtist = value;
          break;
        case 'TCON':
          metadata.genre = value;
          break;
        case 'TYER':
        case 'TDRC':
          const year = parseInt(value);
          if (!isNaN(year)) {
            metadata.year = year;
          }
          break;
        case 'TRCK':
          const trackMatch = value.match(/^(\d+)/);
          if (trackMatch) {
            metadata.trackNumber = parseInt(trackMatch[1]);
          }
          break;
        case 'TPOS':
          const discMatch = value.match(/^(\d+)/);
          if (discMatch) {
            metadata.discNumber = parseInt(discMatch[1]);
          }
          break;
        case 'TCOM':
          metadata.composer = value;
          break;
        case 'COMM':
          metadata.comment = value;
          break;
      }

      offset += frameSize;
    }

    return metadata;
  }

  /**
   * Extract ID3v1 tags (from end of file)
   */
  private extractID3v1(data: Uint8Array): ExtractedMetadata {
    const metadata: ExtractedMetadata = {};

    // ID3v1 tags are in the last 128 bytes
    if (data.length < 128) {
      return metadata;
    }

    const offset = data.length - 128;
    
    // Check for "TAG" header
    if (String.fromCharCode(data[offset], data[offset + 1], data[offset + 2]) !== 'TAG') {
      return metadata;
    }

    // Title (30 bytes)
    const title = this.decodeString(data.slice(offset + 3, offset + 33));
    if (title) metadata.title = title;

    // Artist (30 bytes)
    const artist = this.decodeString(data.slice(offset + 33, offset + 63));
    if (artist) metadata.artist = artist;

    // Album (30 bytes)
    const album = this.decodeString(data.slice(offset + 63, offset + 93));
    if (album) metadata.album = album;

    // Year (4 bytes)
    const yearStr = this.decodeString(data.slice(offset + 93, offset + 97));
    if (yearStr) {
      const year = parseInt(yearStr);
      if (!isNaN(year)) {
        metadata.year = year;
      }
    }

    // Comment (30 bytes, but track number might be in last 2 bytes)
    const commentData = data.slice(offset + 97, offset + 127);
    if (commentData[28] === 0 && commentData[29] !== 0) {
      // ID3v1.1 - has track number
      metadata.trackNumber = commentData[29];
      const comment = this.decodeString(commentData.slice(0, 28));
      if (comment) metadata.comment = comment;
    } else {
      const comment = this.decodeString(commentData);
      if (comment) metadata.comment = comment;
    }

    // Genre (1 byte)
    const genreIndex = data[offset + 127];
    const genre = this.getGenreName(genreIndex);
    if (genre) metadata.genre = genre;

    return metadata;
  }

  /**
   * Decode text frame from ID3v2
   */
  private decodeTextFrame(data: Uint8Array): string {
    if (data.length === 0) return '';

    const encoding = data[0];
    const textData = data.slice(1);

    switch (encoding) {
      case 0: // ISO-8859-1
        return this.decodeString(textData);
      case 1: // UTF-16 with BOM
        return this.decodeUTF16(textData);
      case 2: // UTF-16BE
        return this.decodeUTF16BE(textData);
      case 3: // UTF-8
        return this.decodeUTF8(textData);
      default:
        return this.decodeString(textData);
    }
  }

  /**
   * Decode ISO-8859-1 string
   */
  private decodeString(data: Uint8Array): string {
    let result = '';
    for (let i = 0; i < data.length; i++) {
      if (data[i] === 0) break;
      result += String.fromCharCode(data[i]);
    }
    return result.trim();
  }

  /**
   * Decode UTF-8 string
   */
  private decodeUTF8(data: Uint8Array): string {
    try {
      const decoder = new TextDecoder('utf-8');
      return decoder.decode(data).replace(/\0/g, '').trim();
    } catch {
      return this.decodeString(data);
    }
  }

  /**
   * Decode UTF-16 with BOM
   */
  private decodeUTF16(data: Uint8Array): string {
    try {
      // Check BOM
      if (data.length >= 2) {
        if (data[0] === 0xFF && data[1] === 0xFE) {
          // Little-endian
          const decoder = new TextDecoder('utf-16le');
          return decoder.decode(data.slice(2)).replace(/\0/g, '').trim();
        } else if (data[0] === 0xFE && data[1] === 0xFF) {
          // Big-endian
          const decoder = new TextDecoder('utf-16be');
          return decoder.decode(data.slice(2)).replace(/\0/g, '').trim();
        }
      }
      return this.decodeString(data);
    } catch {
      return this.decodeString(data);
    }
  }

  /**
   * Decode UTF-16BE string
   */
  private decodeUTF16BE(data: Uint8Array): string {
    try {
      const decoder = new TextDecoder('utf-16be');
      return decoder.decode(data).replace(/\0/g, '').trim();
    } catch {
      return this.decodeString(data);
    }
  }

  /**
   * Get synchsafe integer (ID3v2.4)
   */
  private getSynchsafeInt32(data: Uint8Array): number {
    return (data[0] << 21) | (data[1] << 14) | (data[2] << 7) | data[3];
  }

  /**
   * Get regular 32-bit integer
   */
  private getInt32(data: Uint8Array): number {
    return (data[0] << 24) | (data[1] << 16) | (data[2] << 8) | data[3];
  }

  /**
   * Get genre name from ID3v1 genre index
   */
  private getGenreName(index: number): string | undefined {
    const genres = [
      'Blues', 'Classic Rock', 'Country', 'Dance', 'Disco', 'Funk', 'Grunge',
      'Hip-Hop', 'Jazz', 'Metal', 'New Age', 'Oldies', 'Other', 'Pop', 'R&B',
      'Rap', 'Reggae', 'Rock', 'Techno', 'Industrial', 'Alternative', 'Ska',
      'Death Metal', 'Pranks', 'Soundtrack', 'Euro-Techno', 'Ambient',
      'Trip-Hop', 'Vocal', 'Jazz+Funk', 'Fusion', 'Trance', 'Classical',
      'Instrumental', 'Acid', 'House', 'Game', 'Sound Clip', 'Gospel', 'Noise',
      'AlternRock', 'Bass', 'Soul', 'Punk', 'Space', 'Meditative',
      'Instrumental Pop', 'Instrumental Rock', 'Ethnic', 'Gothic', 'Darkwave',
      'Techno-Industrial', 'Electronic', 'Pop-Folk', 'Eurodance', 'Dream',
      'Southern Rock', 'Comedy', 'Cult', 'Gangsta', 'Top 40', 'Christian Rap',
      'Pop/Funk', 'Jungle', 'Native American', 'Cabaret', 'New Wave',
      'Psychadelic', 'Rave', 'Showtunes', 'Trailer', 'Lo-Fi', 'Tribal',
      'Acid Punk', 'Acid Jazz', 'Polka', 'Retro', 'Musical', 'Rock & Roll',
      'Hard Rock'
    ];
    return index < genres.length ? genres[index] : undefined;
  }
}

export default new MetadataExtractor();
